/**
 * Boot helpers for the sql.js persistence impl.
 *
 * Responsibilities:
 *   - load the sql.js WASM module asynchronously (cached across boots)
 *   - read/write the database blob through a pluggable `SqlJsBlobStore`
 *     (IndexedDB in the browser; in-memory for tests)
 *   - apply the schema and run the placeholder migration runner
 *
 * sql.js imports are confined to this folder per ADR-0001 (no SQLite
 * specifics leak into game logic).
 */

import initSqlJs, { type Database, type SqlJsStatic } from 'sql.js';
import schemaSql from './schema.sql?raw';

/**
 * Current schema version. Bump when introducing an incompatible change;
 * add a migration step in `runMigrations` for the old → new transition.
 */
export const SCHEMA_VERSION = 14;

let sqlJsStatic: Promise<SqlJsStatic> | null = null;

export interface LoadSqlJsOptions {
  /** Browser: resolve the wasm URL (e.g. via `import wasm from 'sql.js/dist/sql-wasm.wasm?url'`). */
  readonly locateFile?: (file: string) => string;
  /** Node / tests: pass the raw wasm bytes directly. */
  readonly wasmBinary?: ArrayBuffer;
}

export function loadSqlJs(opts: LoadSqlJsOptions = {}): Promise<SqlJsStatic> {
  if (!sqlJsStatic) {
    const init: Record<string, unknown> = {};
    if (opts.locateFile !== undefined) init.locateFile = opts.locateFile;
    if (opts.wasmBinary !== undefined) init.wasmBinary = opts.wasmBinary;
    sqlJsStatic = initSqlJs(init);
  }
  return sqlJsStatic;
}

/** Test-only: drop the cached static so a fresh boot picks up new options. */
export function resetSqlJsForTests(): void {
  sqlJsStatic = null;
}

// ─── blob store ───────────────────────────────────────────────────────────

/** Pluggable byte store for the exported sql.js database blob. */
export interface SqlJsBlobStore {
  read(): Promise<Uint8Array | null>;
  write(blob: Uint8Array): Promise<void>;
}

/** In-memory blob store. Useful for tests and ephemeral sessions. */
export function createMemoryBlobStore(initial: Uint8Array | null = null): SqlJsBlobStore {
  let blob: Uint8Array | null = initial;
  return {
    async read() {
      return blob;
    },
    async write(next) {
      blob = next;
    },
  };
}

export interface IndexedDbBlobStoreOptions {
  readonly dbName?: string;
  readonly storeName?: string;
  readonly key?: string;
  /** Override the IDB factory (test seam; defaults to `globalThis.indexedDB`). */
  readonly indexedDB?: IDBFactory;
}

const DEFAULT_IDB_DB = 'hex6';
const DEFAULT_IDB_STORE = 'sqljs';
const DEFAULT_IDB_KEY = 'main';

/** IndexedDB-backed blob store. Single key/value record under `key`. */
export function createIndexedDbBlobStore(opts: IndexedDbBlobStoreOptions = {}): SqlJsBlobStore {
  const dbName = opts.dbName ?? DEFAULT_IDB_DB;
  const storeName = opts.storeName ?? DEFAULT_IDB_STORE;
  const key = opts.key ?? DEFAULT_IDB_KEY;
  const factory =
    opts.indexedDB ?? (typeof globalThis !== 'undefined' ? globalThis.indexedDB : undefined);

  if (!factory) {
    throw new Error(
      'IndexedDB not available — pass `indexedDB` explicitly or use createMemoryBlobStore',
    );
  }

  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const req = factory.open(dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

  return {
    async read() {
      const db = await open();
      try {
        return await new Promise<Uint8Array | null>((resolve, reject) => {
          const tx = db.transaction(storeName, 'readonly');
          const store = tx.objectStore(storeName);
          const req = store.get(key);
          req.onsuccess = () => {
            const value = req.result;
            // HEX-82 spike: log blob presence/size on every boot so a live
            // hard-refresh repro can confirm whether IDB retains the DB across
            // sessions. `console.debug` is filtered out of the default console
            // level — silent unless the dev opts into Verbose.
            if (value == null) {
              console.debug(`[hex6/persist] IDB read ${dbName}/${storeName}/${key}: EMPTY`);
              return resolve(null);
            }
            const bytes =
              value instanceof Uint8Array
                ? value
                : value instanceof ArrayBuffer
                  ? new Uint8Array(value)
                  : null;
            if (bytes === null) return reject(new Error('IndexedDB blob has unexpected type'));
            console.debug(
              `[hex6/persist] IDB read ${dbName}/${storeName}/${key}: ${bytes.byteLength} bytes`,
            );
            resolve(bytes);
          };
          req.onerror = () => reject(req.error);
        });
      } finally {
        db.close();
      }
    },
    async write(next) {
      const db = await open();
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(storeName, 'readwrite');
          const store = tx.objectStore(storeName);
          store.put(next, key);
          tx.oncomplete = () => {
            // HEX-82 spike: confirm a flush actually commits to IDB.
            console.debug(
              `[hex6/persist] IDB write ${dbName}/${storeName}/${key}: ${next.byteLength} bytes committed`,
            );
            resolve();
          };
          tx.onabort = () => reject(tx.error);
          tx.onerror = () => reject(tx.error);
        });
      } finally {
        db.close();
      }
    },
  };
}

// ─── schema + migrations ──────────────────────────────────────────────────

export function applySchema(db: Database): void {
  db.exec(schemaSql);
  const rows = db.exec('SELECT version FROM schema_version LIMIT 1');
  const present = rows.length > 0 && (rows[0]?.values.length ?? 0) > 0;
  if (!present) {
    db.run('INSERT INTO schema_version (version) VALUES (?)', [SCHEMA_VERSION]);
  }
}

/**
 * Step-wise migration runner. Walks the persisted DB forward one version at a
 * time until it reaches `SCHEMA_VERSION`. Fresh boots arrive already at the
 * latest version (applySchema seeds the row) and are no-ops. Future versions
 * extend the switch with the migration step for that increment.
 */
export function runMigrations(db: Database): void {
  let current = readSchemaVersion(db);
  while (current < SCHEMA_VERSION) {
    if (current === 1) {
      // v1 → v2: add characters.hp for combat-world integration (HEX-34).
      // Pre-existing rows default to 20 (Starter Warrior baseHp).
      db.run('ALTER TABLE characters ADD COLUMN hp INTEGER NOT NULL DEFAULT 20');
      current = 2;
    } else if (current === 2) {
      // v2 → v3: retire the three-class taxonomy; introduce per-type
      // lifecycleState, crossings, uncovered_*, road_network_id (HEX-29 /
      // ADR-0012). Tile shape inside the chunks BLOB changed
      // incompatibly — drop chunks + tile_metadata and let the world
      // regenerate. Characters survive; just gain `discoveries`.
      db.run('DROP TABLE IF EXISTS chunks');
      db.run('DROP TABLE IF EXISTS tile_metadata');
      db.run(`CREATE TABLE chunks (
        realm  INTEGER NOT NULL,
        cq     INTEGER NOT NULL,
        cr     INTEGER NOT NULL,
        seed   INTEGER NOT NULL,
        tiles  BLOB    NOT NULL,
        PRIMARY KEY (realm, cq, cr)
      )`);
      db.run(`CREATE TABLE tile_metadata (
        realm              INTEGER NOT NULL,
        q                  INTEGER NOT NULL,
        r                  INTEGER NOT NULL,
        discovered_by      TEXT,
        discovered_at      INTEGER,
        uncovered_by       TEXT,
        uncovered_at       INTEGER,
        last_traveller     TEXT,
        last_travelled_at  INTEGER,
        fallen_count       INTEGER NOT NULL DEFAULT 0,
        fallen_characters  TEXT    NOT NULL DEFAULT '[]',
        crossings          INTEGER NOT NULL DEFAULT 0,
        lifecycle_state    TEXT,
        road_network_id    INTEGER,
        PRIMARY KEY (realm, q, r)
      )`);
      db.run('ALTER TABLE characters ADD COLUMN discoveries INTEGER NOT NULL DEFAULT 0');
      current = 3;
    } else if (current === 3) {
      // v3 → v4: biome layer (ADR-0013 / HEX-30) adds `biome` to every
      // tile inside the chunks BLOB. Pre-v4 chunks were generated under a
      // uniform topology — no biome field present — so the world shape
      // itself shifts once biomes seed the impassability grid. Drop the
      // chunks table (and tile_metadata, since old mutations are keyed
      // to coords that may now resolve to lake/mountain). Characters
      // survive untouched. Mirrors the HEX-29 wipe-and-regenerate pattern.
      db.run('DROP TABLE IF EXISTS chunks');
      db.run('DROP TABLE IF EXISTS tile_metadata');
      db.run(`CREATE TABLE chunks (
        realm  INTEGER NOT NULL,
        cq     INTEGER NOT NULL,
        cr     INTEGER NOT NULL,
        seed   INTEGER NOT NULL,
        tiles  BLOB    NOT NULL,
        PRIMARY KEY (realm, cq, cr)
      )`);
      db.run(`CREATE TABLE tile_metadata (
        realm              INTEGER NOT NULL,
        q                  INTEGER NOT NULL,
        r                  INTEGER NOT NULL,
        discovered_by      TEXT,
        discovered_at      INTEGER,
        uncovered_by       TEXT,
        uncovered_at       INTEGER,
        last_traveller     TEXT,
        last_travelled_at  INTEGER,
        fallen_count       INTEGER NOT NULL DEFAULT 0,
        fallen_characters  TEXT    NOT NULL DEFAULT '[]',
        crossings          INTEGER NOT NULL DEFAULT 0,
        lifecycle_state    TEXT,
        road_network_id    INTEGER,
        PRIMARY KEY (realm, q, r)
      )`);
      current = 4;
    } else if (current === 4) {
      // v4 → v5: placeholder economy (HEX-39) adds `characters.gold`.
      // Additive, defaulted to 0; chunks + tile_metadata untouched. Mirrors
      // the HEX-34 hp migration shape.
      db.run('ALTER TABLE characters ADD COLUMN gold INTEGER NOT NULL DEFAULT 0');
      current = 5;
    } else if (current === 5) {
      // v5 → v6: per-character deck-builder inventory (HEX-63 / ADR-0015 §1).
      // Additive nullable TEXT column carrying a JSON SerialisedInventory;
      // chunks + tile_metadata untouched. Pre-v6 rows get NULL and are mapped
      // to the §5/§19 starter on read (`rowToCharacter`) — the migration default
      // lives there so this step stays a pure schema change, free of the combat
      // card data. Mirrors the HEX-34 hp / HEX-39 gold additive shape.
      db.run('ALTER TABLE characters ADD COLUMN inventory TEXT');
      current = 6;
    } else if (current === 6) {
      // v6 → v7: death/legacy archive (HEX-65 / ADR-0015 §7 reframe). Combat
      // defeat now archives the character instead of deleting it — the row
      // survives with its inventory/deck intact and drops out of the active
      // roster via `died_at`. Additive nullable columns; existing rows get NULL
      // `died_at` and read back as alive. chunks + tile_metadata untouched.
      // Mirrors the HEX-34 hp / HEX-39 gold / HEX-63 inventory additive shape.
      db.run('ALTER TABLE characters ADD COLUMN died_at INTEGER');
      db.run('ALTER TABLE characters ADD COLUMN death_realm INTEGER');
      db.run('ALTER TABLE characters ADD COLUMN death_q INTEGER');
      db.run('ALTER TABLE characters ADD COLUMN death_r INTEGER');
      db.run('ALTER TABLE characters ADD COLUMN death_cause TEXT');
      current = 7;
    } else if (current === 7) {
      // v7 → v8: persistent per-shop card stock (HEX-72 / ADR-0015 §6 economy
      // rework). The stock is an additive `cardStock` field on the existing
      // `ShopLifecycleState`, which already round-trips inside the
      // `tile_metadata.lifecycle_state` JSON blob — so there is no DDL change.
      // This step is a pure version marker: pre-v8 shop tiles carry no
      // `cardStock` and lazily roll + persist their shelf on first open (the
      // read-time default lives in `ShopFlow.loadStock`, mirroring the HEX-63
      // inventory-NULL pattern). Existing saves — characters, chunks, metadata —
      // are preserved untouched.
      current = 8;
    } else if (current === 8) {
      // v8 → v9: per-character map-revealed set (HEX-73). A bought map shares the
      // tiles OTHER travellers discovered around a shop — they render for this
      // character (inherited memory) without crediting personal discovery. Stored
      // as an additive nullable TEXT column carrying a JSON array of "q,r" keys,
      // mirroring `explored`; pre-v9 rows get NULL and read back as an empty set
      // (`rowToCharacter`). chunks + tile_metadata untouched. Mirrors the HEX-63
      // inventory / HEX-65 death additive shape.
      db.run('ALTER TABLE characters ADD COLUMN revealed TEXT');
      current = 9;
    } else if (current === 9) {
      // v9 → v10: world clock (HEX-99 / ADR-0018 §5). Adds a new world-scoped,
      // single-row counter table — the overworld turn count (one player step =
      // one tick). Purely additive: a CREATE TABLE, no existing table touched and
      // no row seeded. A pre-v10 save gains the empty table; with no row present
      // `loadWorldTick` reads the default 0, so a legacy world simply resumes its
      // clock from zero. Expressed as a table (not a column) because the clock is
      // world-scoped, not per-character — see schema.sql. Mirrors the additive
      // spirit of the hp / gold / inventory column adds.
      db.run(`CREATE TABLE IF NOT EXISTS world_clock (
        id   INTEGER NOT NULL PRIMARY KEY CHECK (id = 0),
        tick INTEGER NOT NULL DEFAULT 0
      )`);
      current = 10;
    } else if (current === 10) {
      // v10 → v11: per-character run-scoped owned dice (HEX-116 / ADR-0018 §3).
      // The FIRST progression schema bump — dice only; XP/level is a separate,
      // sequential bump (HEX-117) to avoid a concurrent migration. Additive
      // nullable TEXT column carrying a JSON `DieType[]`; chunks + tile_metadata
      // untouched. Pre-v11 rows get NULL and are mapped to the starter d6 set on
      // read (`rowToCharacter`) — the migration default lives there so this step
      // stays a pure schema change. Mirrors the HEX-63 inventory / HEX-73 revealed
      // additive shape exactly.
      db.run('ALTER TABLE characters ADD COLUMN dice TEXT');
      current = 11;
    } else if (current === 11) {
      // v11 → v12: character XP + level (HEX-117 / ADR-0018 §3). The SECOND
      // progression schema bump — sequential after v10→v11 (dice). Two additive
      // nullable INTEGER columns; chunks + tile_metadata untouched. Pre-v12 rows
      // get NULL and are mapped to xp=0, level=1 on read (`rowToCharacter`) so the
      // migration default lives at read-time, mirroring the v10→v11 dice pattern
      // exactly. A NULL level on an archived (dead) character reads back as level 1
      // — the pre-HEX-117 graveyard default.
      db.run('ALTER TABLE characters ADD COLUMN xp INTEGER');
      db.run('ALTER TABLE characters ADD COLUMN level INTEGER');
      current = 12;
    } else if (current === 12) {
      // v12 → v13: town liberation lifecycle (HEX-122 / ADR-0018 §4). Towns gain
      // a `townState` ('evil' | 'liberated' | 'under-threat') and liberation
      // materialises a reserved pillar slot. Both live in the existing
      // `tile_metadata.lifecycle_state` JSON blob — `townState` as an additive
      // field on `TownLifecycleState`, the activated slot as a new
      // `ActivatedPillarSlotLifecycleState` — so there is NO DDL change. Pure
      // version marker (mirrors the v7→v8 cardStock pattern): pre-v13 town tiles
      // carry no `townState` and read back via `getTownState` ('liberated' for the
      // spawn town, 'evil' elsewhere). Existing saves are preserved untouched.
      current = 13;
    } else if (current === 13) {
      // v13 → v14: per-character "seen" set (HEX-128). A tile glimpsed in vision
      // but never crossed used to revert to black once it left the vision disk,
      // leaving black gaps in otherwise-explored terrain. The seen set persists
      // every hex ever in vision so it re-seeds the memory face at boot. Additive
      // nullable TEXT column carrying a JSON array of "q,r" keys, mirroring
      // `explored` / `revealed`; pre-v14 rows get NULL and read back as an empty
      // set (`rowToCharacter`). REVEAL-NOT-CREDIT: never touches the discovery
      // registry or counters. chunks + tile_metadata untouched. Mirrors the HEX-73
      // revealed additive shape exactly.
      db.run('ALTER TABLE characters ADD COLUMN seen TEXT');
      current = 14;
    } else {
      throw new Error(
        `sql.js DB schema version ${current} cannot be migrated (latest is ${SCHEMA_VERSION})`,
      );
    }
    db.run('UPDATE schema_version SET version = ?', [current]);
  }
  if (current > SCHEMA_VERSION) {
    throw new Error(
      `sql.js DB schema version ${current} is not supported (latest is ${SCHEMA_VERSION})`,
    );
  }
}

function readSchemaVersion(db: Database): number {
  const rows = db.exec('SELECT version FROM schema_version LIMIT 1');
  return rows.length > 0 && rows[0]?.values[0]?.[0] != null ? Number(rows[0].values[0][0]) : 0;
}

// ─── boot ─────────────────────────────────────────────────────────────────

export interface BootDatabaseOptions extends LoadSqlJsOptions {
  blobStore: SqlJsBlobStore;
}

/**
 * Boot a `Database`: load WASM, restore the blob from `blobStore` (or start
 * empty), apply the schema, run migrations. Caller owns the returned handle.
 */
export async function bootDatabase(opts: BootDatabaseOptions): Promise<Database> {
  const SQL = await loadSqlJs(opts);
  const blob = await opts.blobStore.read();
  const db = blob ? new SQL.Database(blob) : new SQL.Database();
  applySchema(db);
  runMigrations(db);
  return db;
}
